import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, of, share } from 'rxjs';

/**
 * StorageService is used to wrap session storage functionality with observables/subscribes
 */
@Injectable({
  providedIn: 'root'
})
export class StorageService {
  /** Our session copy of the storage service */
  private _storage: Map<string, any> = new Map<string, any>();

  /** Create a new subject defined to be a map */
  private _onSubject = new Subject<{ key: string, value: any }>();

  /** An observable of the subject map we created. This should fire whenever changes to the session storage occur */
  public changes$ = this._onSubject.asObservable().pipe(share());

  /**
   * Default constructor of the StorageService, which starts an event listener to the "storage" event, or
   * whenever something gets stored in session storage. StorageService initalizes session storage
   */
  constructor() {
    window.addEventListener("storage", this._storageEventListener.bind(this));
    this._updateStorage();
  }

  /**
   * When the application closes, remove the event listener and close the subject
   */
  public ngOnDestroy(): void {
    window.removeEventListener("storage", this._storageEventListener.bind(this));
    this._onSubject.complete();
  }

  /**
   * Store an item (key-value pair) in session storage 
   * 
   * @param key
   * @param value
   */
  public setItem(key: string, value: any): void {
    if (typeof value == "string") {
      sessionStorage.setItem(key, value);
    } else {
      sessionStorage.setItem(key, JSON.stringify(value));
    }
    this._updateStorage();

    this._onSubject.next({ key: key, value: value });
  }

  /**
   * Get an item from the session storage
   * 
   * @param key Key to get value for
   * @returns The value from the key in session storage, undefined if not returned
   */
  public getItem(key: string): any {
    return this._storage.get(key);
  }

  /**
   * Return status of if the key is in the session storage
   * 
   * @param key Key to check for
   * @returns Whether or not the key is in the session storage
   */
  public hasItem(key: string): boolean {
    return this._storage.has(key);
  }

  /**
   * Clear a key from the session storage 
   * 
   * @param key 
   */
  public removeItem(key: string): void {
    sessionStorage.removeItem(key);
    this._updateStorage();

    this._onSubject.next({ key: key, value: null });
  }

  /**
   * Load an item from session storage by returning a promise when it loads
   */
  public async loadItem(key: string): Promise<any> {
    // Test if the value is already present
    let value = this.getItem(key);
    if (value != undefined) return value;

    // If not, subscribe to changes and return the value if ever found
    const get = async () => {
      this.changes$.subscribe(
        (entry: {key: string; value: string}) => {
          // If the key matches, set the value and return
          if (entry.key === key) {
            value = entry.value;
          }
        });
    }
    await get();

    return value;
  }

  /**
   * Get the entirety of the session storage and return it as a map
   * 
   * @returns A list of map entries in session storage
   */
  private _updateStorage(): void {
    let s = new Map<string, any>();

    // Loop for each item in the session storage map
    for (let i = 0; i < sessionStorage.length; i++) {
      // Get the key and value
      const key = sessionStorage.key(i)
      const value = sessionStorage.getItem(key || "")

      // Don't add undefined keys (should never happen but need it for ts checks)
      if (key == undefined) {
        continue;
      }

      // Add it to the list of entries to return
      s.set(key, value);
    }

    this._storage = s;
  }

  /**
   * Listen to storage events and wait for session storage events 
   * @param event 
   * @returns 
   */
  private _storageEventListener(event: StorageEvent): void {
    // We only care about sessionStorage events
    if (event.storageArea != sessionStorage) {
      return;
    }

    // Null keys aren't allowed. If a valid key, update the session storage
    const key = event.key;
    if (key == undefined) {
      return;
    }
    this._updateStorage();

    // The value of the stored item is either an object in JSON or a string. Assume it is json
    const value = JSON.parse(event.newValue || "");

    // Update the subject that we have a new entry (default to string value on bad json read)
    this._onSubject.next({ key: key, value: value || event.newValue });
  }
}
